/**
 * Text helpers for the multi-party post-call views (callInsights.multiParty
 * and the raised_by / handled_by fields on concern items). Single-party calls
 * carry none of these fields and every helper returns nothing for them.
 */
import { ENGAGEMENT_LABELS, INFERRED_ROLE_LABELS } from '../../config/participants';
import type { MultiPartyInsights, ObjFaqItem } from '../adherence/types';

/** "Raised by Priya · Handled by Alex and Jordan", or null on a single-party call. */
export function describeConcernPeople(item: Pick<ObjFaqItem, 'raised_by' | 'handled_by'>): string | null {
  const raisedBy = item?.raised_by?.name;
  const handledBy = (item?.handled_by ?? []).map((person) => person?.name).filter(Boolean) as string[];
  if (!raisedBy && handledBy.length === 0 && item?.handled_by === undefined) return null;
  const parts: string[] = [];
  if (raisedBy) parts.push(`Raised by ${raisedBy}`);
  parts.push(handledBy.length > 0 ? `Handled by ${joinNames(handledBy)}` : 'No rep answered it');
  return parts.join(' · ');
}

export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export const inferredRoleLabel = (role?: string | null): string =>
  INFERRED_ROLE_LABELS[role ?? ''] ?? INFERRED_ROLE_LABELS.unclear;

export const engagementLabel = (engagement?: string | null): string | null =>
  engagement ? ENGAGEMENT_LABELS[engagement] ?? null : null;

/** "ready_to_commit" -> "ready to commit" (the Sales Intelligence section's own formatting). */
export const signalText = (signal?: string | null): string | null => (signal ? signal.replace(/_/g, ' ') : null);

/** 2+ salespeople or 2+ customers took part: the calls whose Details opens on Participants. */
export const isMultiPartyCall = (multiParty?: Pick<MultiPartyInsights, 'reps' | 'customers'> | null): boolean =>
  (multiParty?.reps?.length ?? 0) >= 2 || (multiParty?.customers?.length ?? 0) >= 2;

/** Anyone profiled on the call: the calls whose Summary shows the Participants card. */
export const hasParticipants = (participants?: Pick<MultiPartyInsights, 'reps' | 'customers'> | null): boolean =>
  (participants?.reps?.length ?? 0) + (participants?.customers?.length ?? 0) > 0;
